import { useState, useEffect } from "react";
import { toast } from "sonner";
import { useSettings } from "../contexts/SettingsContext";
import MediaUploader from "./MediaUploader";
import api, { formatErr } from "../lib/api";

const FIELDS = [
  { group: "Branding", fields: [
    { key: "brand_name", label: "Brand Name", type: "text" },
  ]},
  { group: "Logo", fields: [
    { key: "logo_url", label: "Logo", type: "image" },
  ]},
  { group: "Homepage — Hero", fields: [
    { key: "hero_image", label: "Hero Background Image (fallback)", type: "image" },
    { key: "hero_video", label: "Hero Background Video (loops muted; replaces image when set)", type: "video" },
    { key: "hero_eyebrow", label: "Eyebrow (small caps line above title)", type: "text" },
    { key: "hero_title_line_1", label: "Hero Title — Line 1", type: "text" },
    { key: "hero_title_line_2", label: "Hero Title — Line 2 (gold italic)", type: "text" },
    { key: "hero_cta_label", label: "Hero Button Text", type: "text" },
  ]},
  { group: "Homepage — About", fields: [
    { key: "about_image", label: "About Image", type: "image" },
    { key: "about_eyebrow", label: "Eyebrow", type: "text" },
    { key: "about_title", label: "Title", type: "text" },
    { key: "about_body", label: "Body Text", type: "textarea" },
  ]},
  { group: "Homepage — Behind the Atelier (video section)", fields: [
    { key: "atelier_video", label: "Atelier Video (looping, shown below About section)", type: "video" },
    { key: "atelier_video_title", label: "Section Title", type: "text" },
    { key: "atelier_video_body", label: "Section Body", type: "textarea" },
  ]},
  { group: "Homepage — CTA Banner", fields: [
    { key: "cta_image", label: "CTA Background Image", type: "image" },
    { key: "cta_eyebrow", label: "Eyebrow", type: "text" },
    { key: "cta_title", label: "Title", type: "text" },
    { key: "cta_subtitle", label: "Subtitle", type: "textarea" },
    { key: "cta_coupon_code", label: "Highlighted Coupon Code", type: "text" },
  ]},
  { group: "Contact", fields: [
    { key: "contact_email", label: "Email", type: "text" },
    { key: "contact_phone", label: "Phone", type: "text" },
    { key: "contact_address", label: "Address", type: "textarea" },
    { key: "instagram_url", label: "Instagram URL", type: "text" },
  ]},
  { group: "Footer", fields: [
    { key: "footer_tagline", label: "Tagline", type: "textarea" },
  ]},
  { group: "Marketing Pixels", fields: [
    { key: "meta_pixel_id", label: "Meta (Facebook) Pixel ID", type: "text" },
    { key: "clarity_id", label: "Microsoft Clarity Project ID", type: "text" },
  ]},
  { group: "Email (Resend)", fields: [
    { key: "sender_email", label: "Sender Email (FROM address — must be on a verified Resend domain)", type: "text", placeholder: "orders@goshifashion.com" },
    { key: "admin_notification_email", label: "Admin Notification Email (where new-order alerts arrive)", type: "text", placeholder: "support@goshifashion.com" },
    { key: "brand_name", label: "Brand Name (shown in emails)", type: "text", placeholder: "GOSHI" },
  ]},
];

export default function SiteSettingsAdmin() {
  const { settings, save, loaded } = useSettings();
  const [draft, setDraft] = useState({});
  const [busy, setBusy] = useState(false);

  useEffect(() => { if (loaded) setDraft(settings); }, [loaded, settings]);

  const set = (k, v) => setDraft(d => ({ ...d, [k]: v }));

  const setImage = (k, images) => {
    set(k, images[0] || "");
  };

  const setVideo = (k, videos) => {
    const url = videos[0] || "";
    // When user removes the video, wipe the auto-generated variants too
    if (!url) {
      setDraft(d => ({ ...d, [k]: "", [`${k}_webm`]: "", [`${k}_poster`]: "" }));
    } else {
      set(k, url);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await save(draft);
      toast.success("Site settings updated");
    } catch (err) {
      toast.error(formatErr(err));
    } finally {
      setBusy(false);
    }
  };

  const reset = () => setDraft(settings);

  if (!loaded) return <p className="text-muted text-xs uppercase tracking-[0.2em]">Loading…</p>;

  return (
    <form onSubmit={submit} className="space-y-12 max-w-3xl" data-testid="site-settings-form">
      <p className="text-muted text-sm">
        Changes go live immediately on the public site. Upload images via the dropzones — they're stored in your media library.
      </p>

      {FIELDS.map(group => (
        <fieldset key={group.group} className="bg-[#141414] p-8 space-y-5 border border-[rgba(244,244,240,0.04)]">
          <legend className="text-xs uppercase tracking-[0.25em] text-gold px-3">{group.group}</legend>

          {group.fields.map(f => (
            <div key={f.key} className="space-y-2">
              <label className="text-xs uppercase tracking-[0.2em] text-muted block">{f.label}</label>

              {f.type === "text" && (
                <input
                  className="input-line"
                  value={draft[f.key] || ""}
                  onChange={(e) => set(f.key, e.target.value)}
                  data-testid={`settings-${f.key}`}
                />
              )}

              {f.type === "textarea" && (
                <textarea
                  className="input-line resize-none"
                  rows={4}
                  value={draft[f.key] || ""}
                  onChange={(e) => set(f.key, e.target.value)}
                  data-testid={`settings-${f.key}`}
                />
              )}

              {f.type === "image" && (
                <>
                  <MediaUploader
                    images={draft[f.key] ? [draft[f.key]] : []}
                    videos={[]}
                    accept="image"
                    onChange={({ images }) => setImage(f.key, images)}
                  />
                  {!draft[f.key] && (
                    <p className="text-[10px] uppercase tracking-[0.18em] text-muted/70">
                      No image set — public site shows the default fallback.
                    </p>
                  )}
                </>
              )}

              {f.type === "video" && (
                <>
                  <MediaUploader
                    images={[]}
                    videos={draft[f.key] ? [draft[f.key]] : []}
                    accept="video"
                    onChange={({ videos }) => setVideo(f.key, videos)}
                    onVideoOptimized={({ webm_url, poster_url }) => {
                      // Persist the derived variants alongside the raw mp4 URL
                      setDraft(d => ({
                        ...d,
                        [`${f.key}_webm`]: webm_url || "",
                        [`${f.key}_poster`]: poster_url || "",
                      }));
                    }}
                  />
                  {draft[f.key] && (draft[`${f.key}_webm`] || draft[`${f.key}_poster`]) && (
                    <p className="text-[10px] uppercase tracking-[0.18em] text-gold mt-1">
                      ✓ Optimised — WebM {draft[`${f.key}_webm`] ? "ready" : "—"} · Poster {draft[`${f.key}_poster`] ? "ready" : "—"}
                    </p>
                  )}
                  {!draft[f.key] && (
                    <p className="text-[10px] uppercase tracking-[0.18em] text-muted/70">
                      No video set. Recommended: MP4, muted, ≤ 25 MB, 8–20 seconds looped. WebM + poster are generated automatically on upload.
                    </p>
                  )}
                </>
              )}
            </div>
          ))}
        </fieldset>
      ))}

      <fieldset className="bg-[#141414] p-8 space-y-5 border border-[rgba(244,244,240,0.04)]">
        <legend className="text-xs uppercase tracking-[0.25em] text-gold px-3">Video Backfill</legend>
        <p className="text-xs text-muted">
          Regenerate WebM + poster for any legacy MP4 uploads that never got optimised. Runs automatically once a day (10:00 UTC); trigger manually below.
        </p>
        <VideoBackfillPanel />
      </fieldset>

      <fieldset className="bg-[#141414] p-8 space-y-5 border border-[rgba(244,244,240,0.04)]">
        <legend className="text-xs uppercase tracking-[0.25em] text-gold px-3">Email Preview</legend>
        <p className="text-xs text-muted">
          Send a sample order-confirmation email to verify the template & your Resend setup before the first real order.
        </p>
        <TestEmailRow />
      </fieldset>

      <fieldset className="bg-[#141414] p-8 space-y-5 border border-[rgba(244,244,240,0.04)]">
        <legend className="text-xs uppercase tracking-[0.25em] text-gold px-3">GST Invoice</legend>
        <p className="text-xs text-muted">Used on Indian tax invoices downloaded from Admin → Orders → GST button. Product prices are treated as GST-inclusive and the taxable value is back-calculated using the rate below.</p>
        <div className="grid sm:grid-cols-2 gap-5">
          <label className="block">
            <span className="text-[10px] uppercase tracking-[0.22em] text-muted block mb-1.5">Business Name</span>
            <input className="input-line" value={draft.gst_business_name || ""} onChange={e => set("gst_business_name", e.target.value)} data-testid="gst-business-name" />
          </label>
          <label className="block">
            <span className="text-[10px] uppercase tracking-[0.22em] text-muted block mb-1.5">GSTIN</span>
            <input className="input-line" value={draft.gstin || ""} onChange={e => set("gstin", e.target.value.toUpperCase())} data-testid="gst-gstin" />
          </label>
          <label className="block">
            <span className="text-[10px] uppercase tracking-[0.22em] text-muted block mb-1.5">Business Email</span>
            <input className="input-line" value={draft.gst_email || ""} onChange={e => set("gst_email", e.target.value)} data-testid="gst-email" />
          </label>
          <label className="block">
            <span className="text-[10px] uppercase tracking-[0.22em] text-muted block mb-1.5">Proprietor Name</span>
            <input className="input-line" value={draft.gst_proprietor_name || ""} onChange={e => set("gst_proprietor_name", e.target.value)} data-testid="gst-proprietor" />
          </label>
          <label className="block sm:col-span-2">
            <span className="text-[10px] uppercase tracking-[0.22em] text-muted block mb-1.5">Business Address (single line)</span>
            <input className="input-line" value={draft.gst_business_address || ""} onChange={e => set("gst_business_address", e.target.value)} data-testid="gst-address" />
          </label>
          <label className="block">
            <span className="text-[10px] uppercase tracking-[0.22em] text-muted block mb-1.5">Default HSN Code</span>
            <input className="input-line" value={draft.gst_hsn_code || ""} onChange={e => set("gst_hsn_code", e.target.value)} data-testid="gst-hsn" />
          </label>
          <label className="block">
            <span className="text-[10px] uppercase tracking-[0.22em] text-muted block mb-1.5">GST Threshold (₹ per piece)</span>
            <input type="number" step="1" min="0" className="input-line" value={draft.gst_threshold_rupees ?? 2500} onChange={e => set("gst_threshold_rupees", parseFloat(e.target.value) || 0)} data-testid="gst-threshold" />
          </label>
          <label className="block">
            <span className="text-[10px] uppercase tracking-[0.22em] text-muted block mb-1.5">GST Rate ≤ Threshold (%)</span>
            <input type="number" step="0.5" min="0" max="28" className="input-line" value={draft.gst_rate_below_percent ?? 5} onChange={e => set("gst_rate_below_percent", parseFloat(e.target.value) || 0)} data-testid="gst-rate-below" />
          </label>
          <label className="block">
            <span className="text-[10px] uppercase tracking-[0.22em] text-muted block mb-1.5">GST Rate &gt; Threshold (%)</span>
            <input type="number" step="0.5" min="0" max="28" className="input-line" value={draft.gst_rate_above_percent ?? 18} onChange={e => set("gst_rate_above_percent", parseFloat(e.target.value) || 0)} data-testid="gst-rate-above" />
          </label>
        </div>
        <p className="text-[10px] uppercase tracking-[0.18em] text-muted">
          Tiered GST is per-piece: items ≤ threshold get the lower rate, items above get the higher rate. Prices on the site are treated as GST-inclusive and back-calculated for the invoice.
        </p>
      </fieldset>

      <div className="flex gap-3 sticky bottom-6 bg-[#0C0C0C] p-4 border border-[rgba(244,244,240,0.08)] z-10">
        <button type="submit" className="btn-gold flex-1" disabled={busy} data-testid="save-settings-btn">
          {busy ? "Saving…" : "Save Site Settings"}
        </button>
        <button type="button" onClick={reset} className="btn-outline">Reset</button>
      </div>
    </form>
  );
}

function VideoBackfillPanel() {
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/admin/videos/backfill/status");
      setStatus(data);
    } catch (e) {
      // silent — panel just doesn't display latest state
    }
  };

  useEffect(() => {
    load();
    const int = setInterval(load, 4000);
    return () => clearInterval(int);
  }, []);

  const runNow = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/videos/backfill/run-now");
      if (data.already_running) toast.message("Backfill is already running");
      else toast.success("Backfill started");
      await load();
    } catch (e) {
      toast.error(formatErr(e));
    } finally {
      setBusy(false);
    }
  };

  const last = status?.last;
  return (
    <div className="space-y-4" data-testid="video-backfill-panel">
      <div className="flex flex-wrap items-center gap-3 text-xs">
        <span className="uppercase tracking-[0.2em] text-muted">Missing WebM:</span>
        <span className="text-gold font-mono text-sm" data-testid="video-backfill-missing">
          {status?.missing_count ?? "—"}
        </span>
        {status?.running && (
          <span className="text-[10px] uppercase tracking-[0.22em] text-gold animate-pulse" data-testid="video-backfill-running">
            ● Running…
          </span>
        )}
      </div>

      <button
        type="button"
        onClick={runNow}
        disabled={busy || status?.running}
        className="btn-outline text-xs"
        data-testid="video-backfill-run-btn"
      >
        {busy || status?.running ? "Running…" : `Backfill Now (batch of ${status?.batch_size ?? 10})`}
      </button>

      {last && (
        <div className="text-[10px] uppercase tracking-[0.18em] text-muted flex flex-wrap gap-x-5 gap-y-1" data-testid="video-backfill-last">
          <span>Last run · {new Date(last.finished_at).toLocaleString()}</span>
          <span>Processed <b className="text-text">{last.processed}</b></span>
          <span>Converted <b className="text-gold">{last.converted}</b></span>
          {last.failed > 0 && <span>Failed <b className="text-red-400">{last.failed}</b></span>}
          {last.skipped > 0 && <span>Skipped {last.skipped}</span>}
        </div>
      )}
    </div>
  );
}

function TestEmailRow() {
  const [email, setEmail] = useState("");
  const [sending, setSending] = useState(false);
  const send = async () => {
    if (!email) { toast.error("Enter a recipient email"); return; }
    setSending(true);
    try {
      const { data } = await api.post("/admin/test-email", { to: email });
      if (data.ok) {
        toast.success(`Sample email sent to ${data.to || email}`);
      } else if (data.skipped) {
        toast.message("Email skipped", { description: data.reason });
      } else {
        const raw = data.error || "Unknown error";
        const friendly = /testing emails to your own email/i.test(raw)
          ? "Resend is in sandbox mode — verify your domain at resend.com/domains, then set Sender Email in Admin → Settings → Email (Resend)."
          : raw;
        toast.error(`Email send failed: ${friendly}`, { duration: 9000 });
      }
    } catch (err) {
      toast.error(formatErr(err));
    } finally {
      setSending(false);
    }
  };
  return (
    <div className="flex flex-col sm:flex-row gap-3">
      <input
        type="email"
        placeholder="recipient@example.com"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        className="input-line flex-1"
        data-testid="test-email-input"
      />
      <button
        type="button"
        onClick={send}
        className="btn-outline"
        disabled={sending}
        data-testid="send-test-email-btn"
      >
        {sending ? "Sending…" : "Send Test Email"}
      </button>
    </div>
  );
}
